import React, { useRef } from "react";
import { Text } from "@sitecore-jss/sitecore-jss-react";
import Slider from "react-slick";
import CarouselDots from "../../react-only-components/CarouselDots";
import CustomArrow from "../../react-only-components/CarouselArrow";
import { StringField } from "../../types/types";
import CardGridCard from "../../react-only-components/CardGridCard";
import { CardProps } from "../../types/types";
import LazySection from "../../react-only-components/LazySection";
import { windowService } from "../../utils/window.utils";
import "./index.scss";

// const cardGridMockData: CardGridProps = {
//   fields: {
//     Title: { value: "Experience Case" },
//     Subtitle: {
//       value: "Find Everything you're looking for with our tools and resources",
//     },
//     Cards: [
//       {
//         fields: {
//           Image: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_0.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           imageMobile: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_0.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           Title: { value: "Build & Price" },
//           Subtitle: { value: "Customize the perfect machine for your needs" },
//           Link: { value: { href: "", text: "Build & Price" } },
//           Date: { value: "2021-11-01T18:30:00Z" },
//         },
//       },
//       {
//         fields: {
//           Image: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_1.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           imageMobile: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_1.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           Title: { value: "Compare Machines" },
//           Subtitle: {
//             value: "View machine specifications and compare multiple machines",
//           },
//           Link: { value: { href: "", text: "Compare machines" } },
//           Date: { value: "2021-11-01T18:30:00Z" },
//         },
//       },
//       {
//         fields: {
//           Image: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_2.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           imageMobile: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_2.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           Title: { value: "Service & Support" },
//           Subtitle: {
//             value:
//               "Find the answers you need with troubleshooting support, operator's manuals, and how-to tutorials. We work hard to provide our customers an excellent after-sales service",
//           },
//           Link: { value: { href: "", text: "Explore support" } },
//           Date: { value: "2021-11-01T18:30:00Z" },
//         },
//       },
//       {
//         fields: {
//           Image: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_3.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           imageMobile: {
//             value: {
//               src: "//localhost:8085/images/CardGrid_3.jpg",
//               alt: "image description",
//               width: 720,
//               height: 448,
//             },
//           },
//           Title: { value: "Spare parts" },
//           Subtitle: {
//             value:
//               "Shop genuine  parts made for your machine and designed for peak performance",
//           },
//           Link: { value: { href: "", text: "Shop parts" } },
//           Date: { value: "2021-11-01T18:30:00Z" },
//         },
//       },
//     ],
//   },
// };

type CardGridProps = {
  fields?: {
    Title?: StringField;
    Subtitle?: StringField;
    Cards?: CardProps[];
  };
};

// use only in useEffect / functions that runs only client-side
const getSlidesToShow = () => {
  const breakpointMd = windowService().matchMedia("(min-width: 450px)").matches;
  if (breakpointMd) {
    const breakpointLg = windowService().matchMedia(
      "(min-width: 1000px)"
    ).matches;
    if (breakpointLg) {
      return 4;
    }
    return 2;
  }
  return 1.2;
};

const CardGrid = ({ fields }: CardGridProps) => {
  // const dataSource =
  //   fields?.Title && fields?.Subtitle ? {...fields} : {...cardGridMockData.fields};
  const { Title: title, Subtitle: subtitle, Cards: cards } = {...fields};
  const generateCards = (cards ?? []).map((item, i) => (
    <CardGridCard {...item} key={i} />
  ));
  const settings = {
    arrows: true,
    dots: true,
    slidesToScroll: 1,
    slidesToShow: 4,
    nextArrow: <CustomArrow direction="next" />,
    prevArrow: <CustomArrow direction="previous" />,
    customPaging: (_) => <CarouselDots />,
    infinite: false,
    responsive: [
      { breakpoint: 1000, settings: { slidesToShow: 2 } },
      { breakpoint: 450, settings: { slidesToShow: 1.2 } },
    ],
  };

  const cardGridWrapperRef = useRef(null);

  const activeClass = "slick-active";

  const isLastDecimalSlide = (currentSlide) => {
    const slidesToShow = getSlidesToShow();
    const slideTotal = cards?.length ?? 0;
    return +currentSlide.toFixed(2) === +(slideTotal - slidesToShow).toFixed(2);
  };

  const beforeChange = function (prevSlide, nextSlide): void {
    if (cardGridWrapperRef.current) {
      const lastPaginationEl = (
        cardGridWrapperRef.current as HTMLElement
      ).querySelector(`.slick-dots li:last-child`);
      if (lastPaginationEl && isLastDecimalSlide(nextSlide)) {
        lastPaginationEl?.classList.add(activeClass);
      } else {
        lastPaginationEl?.classList.remove(activeClass);
      }
    }
  };

  const afterChange = function (currentSlide: number): void {
    if (cardGridWrapperRef.current) {
      const lastPaginationEl = (
        cardGridWrapperRef.current as HTMLElement
      ).querySelector(`.slick-dots li:last-child`);
      if (lastPaginationEl && isLastDecimalSlide(currentSlide)) {
        lastPaginationEl?.classList.add(activeClass);
      } else {
        lastPaginationEl?.classList.remove(activeClass);
      }
    }
  };

  return (
    <LazySection>
      <div className="card-grid-wrapper" ref={cardGridWrapperRef}>
        <div className="card-grid">
          <div className="card-grid__text">
            <Text tag="h2" field={title} />
            <Text tag="p" field={subtitle} className={"card-grid__subtitle"} />
          </div>
          <Slider
            {...settings}
            beforeChange={beforeChange}
            afterChange={afterChange}
          >
            {generateCards}
          </Slider>
        </div>
      </div>
    </LazySection>
  );
};

export default CardGrid;
